import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./use-auth";
import { apiRequest } from "../lib/queryClient";

/** Permission keys returned by GET /api/protected/role-permissions/me */
export type PermissionsMap = Record<string, boolean>;

/**
 * Feature -> permission keys that grant visibility (any one of these allows the menu item).
 * Used to hide sidebar items the user's role cannot access.
 */
export const FEATURE_PERMISSION_KEYS: Record<string, string[]> = {
  dashboard: [], // always visible when authenticated
  calendar: ["calendar.view_all"],
  clients: ["clients.view_all"],
  estimates: ["estimates.view_all", "estimates.view_own_only"],
  invoices: ["invoices.view_all"],
  projects: ["projects.view_all"],
  materials: ["projects.view_all"], // use projects for now
  services: ["settings.view"], // services under settings visibility
  timeclock: ["timeclock.access"],
  agents: ["projects.view_all"], // agents often with projects
  workers: ["projects.view_all"], // workers list — Business plan only, same view as projects for now
  "ai-assistant": [], // no permission key yet - show if plan allows
  settings: ["settings.view"],
};

export function usePermissions() {
  const { user } = useAuth();
  const role = (user as { role?: string })?.role;
  const isOwnerOrAdmin = role === "owner" || role === "admin" || role === "contractor";

  const { data, isLoading } = useQuery<{ permissions: PermissionsMap }>({
    queryKey: ["/api/protected/role-permissions/me"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/protected/role-permissions/me");
      return res.json();
    },
    enabled: !!user?.id && !isOwnerOrAdmin,
  });

  const permissions = data?.permissions ?? {};
  const hasPermission = (key: string) => (isOwnerOrAdmin ? true : !!permissions[key]);
  const canAccessFeature = (feature: string) => {
    if (isOwnerOrAdmin) return true;
    // While permissions are loading, don't restrict (prevents redirect to dashboard + sidebar collapse)
    if (isLoading) return true;
    const keys = FEATURE_PERMISSION_KEYS[feature];
    if (!keys || keys.length === 0) return true;
    return keys.some((k) => permissions[k]);
  };

  return {
    permissions,
    isLoading: !!user?.id && !isOwnerOrAdmin && isLoading,
    hasPermission,
    canAccessFeature,
    isOwnerOrAdmin,
  };
}
